
import { Component, Watch, Prop } from 'vue-property-decorator';
import { toDataModel, setState, getChildModel, updateModel } from 'ah-common-lib/src/form/helpers';
import {
  Address,
  AboutUsReference,
  ClientType,
  UserCreationRequest,
  BaseRegistrationModel,
  aboutUsReferenceOptions,
} from 'ah-api-gateways';
import { SecurePasswordChecker } from 'ah-common-lib/src/form/components';
import { FormEvent, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { useSettingsStore } from '@/app/store/settingsModule';
import WithRequestManager from 'ah-common-lib/src/requestManager/WithRequestManager.vue';
import RegistrationAddressForm from '../common/RegistrationAddressForm.vue';
import { accountAccessFM } from '@/app/helpers/registration/forms';
import { cloneDeep } from 'lodash';
import { submitUser } from '@/app/helpers/registration/requests';

/**
 * Representative account access form
 */
@Component({
  components: { SecurePasswordChecker, RegistrationAddressForm },
})
export default class PersonalInformationForm extends WithRequestManager {
  @Prop({ required: true }) model!: Partial<BaseRegistrationModel>;

  @Prop({ required: true }) clientReferral?: string;

  private accountAccessFM = accountAccessFM();

  private accountAccessValidation: FormValidation | null = null;

  private address: Address | null = null;

  private countryValidation: FormValidation | null = null;

  private addressValidation: FormValidation | null = null;

  get settingsStore() {
    return useSettingsStore();
  }

  get calculatedModel(): Partial<BaseRegistrationModel> {
    const { reference, referenceSelection, ...applicant } = toDataModel(this.accountAccessFM);
    return {
      ...this.model,
      reference: referenceSelection === AboutUsReference.OTHER ? reference : referenceSelection,
      applicant: {
        ...this.model.applicant,
        ...applicant,
      },
      address: { ...this.model.address!, ...this.address },
    };
  }

  get userCreationRequest(): UserCreationRequest | null {
    if (!this.address) return null;
    let { nationality, ...address } = this.address;

    return {
      applicant: this.calculatedModel.applicant!,
      registrationData: {
        questionnaireAnswer: this.calculatedModel.reference?.length ? this.calculatedModel.reference : undefined,
        address,
        nationality: nationality!,
        clientReferral: this.clientReferral,
      },
    };
  }

  get isFormInvalid() {
    return (
      !!this.accountAccessValidation?.$invalid ||
      !!this.countryValidation?.$invalid ||
      !!this.addressValidation?.$invalid
    );
  }

  emitUpdate() {
    this.$emit('update:model', this.calculatedModel);
  }

  onAddressChange(address: Address) {
    this.address = {
      ...this.model.address,
      ...address,
      countryCode: this.model.address?.countryCode!,
    };
    delete (this.address as any).countryName;
    this.emitUpdate();
  }

  onFormEvent(formEvent: FormEvent) {
    if (formEvent.event === 'form-field-set-value') {
      setState(this.accountAccessFM, 'errors', [], true);
      this.emitUpdate();

      if (formEvent.model.$name === 'referenceSelection') {
        const field = getChildModel(this.accountAccessFM, 'reference')!;
        setState(field, 'hidden', this.accountAccessFM.referenceSelection !== AboutUsReference.OTHER);
        setState(field, 'required', this.accountAccessFM.referenceSelection === AboutUsReference.OTHER);
        this.accountAccessFM.reference = '';
        if (this.accountAccessValidation?.reference) {
          this.accountAccessValidation.reference.$reset();
        }
      }
    }
  }

  submit() {
    this.requestManager
      .sameOrCancelAndNew(
        'registerUser',
        submitUser(this.model.clientType ?? ClientType.INDIVIDUAL, this.userCreationRequest!, this.accountAccessFM)
      )
      .subscribe(
        () => this.$emit('proceed'),
        () => this.$emit('failure')
      );
  }

  @Watch('model', { immediate: true })
  onModelChange() {
    updateModel(this.accountAccessFM, { ...this.model?.applicant, reference: this.model?.reference });
    this.address = cloneDeep(this.model?.address ?? null);

    if (!!this.model?.reference?.length) {
      if (
        aboutUsReferenceOptions
          .map((obj) => obj.value)
          .filter((v) => v !== AboutUsReference.OTHER)
          .includes(this.model.reference)
      ) {
        this.accountAccessFM.referenceSelection = this.model.reference;
      } else {
        this.accountAccessFM.referenceSelection = AboutUsReference.OTHER;
      }
    }
  }
}
